import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { shipmentsAPI, warehousesAPI, locationsAPI } from '@/lib/api';
import { formatDateShort, formatTime } from '@/lib/utils';
import { 
  Send, 
  Package, 
  Store,
  Calendar,
  TrendingUp,
  Filter,
  Clock,
  CheckCircle,
  X,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ExportJustoButton } from '@/components/ExportJustoButton';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';

const AdminShipmentsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [stats, setStats] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [periodDays, setPeriodDays] = useState('30');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Colores para el gráfico
  const COLORS = ['#E07B54', '#F5C85D', '#4CAF50', '#2196F3', '#9C27B0', '#FF5722', '#607D8B', '#795548'];

  // Cargar datos
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedWarehouse !== 'all') params.warehouse_id = selectedWarehouse;
      if (selectedLocation !== 'all') params.location_id = selectedLocation;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      params.limit = 200;
      
      const [shipmentsRes, statsRes, warehousesRes, locationsRes] = await Promise.all([
        shipmentsAPI.getAll(params),
        shipmentsAPI.getStats({ 
          warehouse_id: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
          days: parseInt(periodDays) 
        }),
        warehousesAPI.getActive(),
        locationsAPI.getAll(true)
      ]);
      
      setShipments(shipmentsRes.data);
      setStats(statsRes.data);
      setWarehouses(warehousesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouse, selectedLocation, startDate, endDate, periodDays]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Datos para gráfico de barras por local
  const chartDataByLocation = useMemo(() => {
    if (!stats?.shipments_by_location) return [];
    return stats.shipments_by_location.map((item, index) => ({
      name: item.location_name,
      envios: item.count,
      cantidad: item.total_quantity,
      fill: COLORS[index % COLORS.length]
    }));
  }, [stats]);

  // Datos para gráfico de tendencia diaria
  const chartDataByDay = useMemo(() => {
    if (!stats?.shipments_by_day) return [];
    return stats.shipments_by_day.map(item => ({
      date: item.date.substring(5), // Solo MM-DD
      envios: item.count,
      cantidad: item.total_quantity
    }));
  }, [stats]);

  // Filtrar envíos mostrados
  const filteredShipments = useMemo(() => {
    let filtered = shipments;
    
    if (selectedWarehouse !== 'all') {
      filtered = filtered.filter(s => s.warehouse_id === selectedWarehouse);
    }
    
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(s => s.location_id === selectedLocation);
    }
    
    return filtered;
  }, [shipments, selectedWarehouse, selectedLocation]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-shipments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Envíos a Locales</h1>
          <p className="text-muted-foreground mt-1">Control y estadísticas de envíos desde bodegas</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Envíos</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_shipments || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">últimos {periodDays} días</p>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Send className="w-6 h-6 text-primary" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Unidades Enviadas</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats?.total_quantity || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">últimos {periodDays} días</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Locales Atendidos</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">
                {stats?.shipments_by_location?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <Store className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Promedio Diario</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">
                {stats?.total_shipments && periodDays 
                  ? (stats.total_shipments / parseInt(periodDays)).toFixed(1) 
                  : '0'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">envíos/día</p>
            </div>
            <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[180px]">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Bodega</label>
            <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las bodegas</SelectItem>
                {warehouses.map(wh => (
                  <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[180px]">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Local</label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los locales</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[140px]">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Período</label>
            <Select value={periodDays} onValueChange={setPeriodDays}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Últimos 7 días</SelectItem>
                <SelectItem value="30">Últimos 30 días</SelectItem>
                <SelectItem value="60">Últimos 60 días</SelectItem>
                <SelectItem value="90">Últimos 90 días</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de barras por local */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Envíos por Local
          </h3>
          {chartDataByLocation.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartDataByLocation} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fontSize: 12 }} 
                  width={100}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [
                    value, 
                    name === 'envios' ? 'Envíos' : 'Cantidad'
                  ]}
                />
                <Bar dataKey="envios" name="Envíos" radius={[0, 4, 4, 0]}>
                  {chartDataByLocation.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No hay datos para mostrar
            </div>
          )}
        </motion.div>

        {/* Gráfico de tendencia diaria */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Tendencia Diaria
          </h3>
          {chartDataByDay.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartDataByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [
                    value, 
                    name === 'envios' ? 'Envíos' : 'Unidades'
                  ]}
                />
                <Legend />
                <Bar dataKey="envios" name="Envíos" fill="#E07B54" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cantidad" name="Unidades" fill="#F5C85D" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No hay datos para mostrar
            </div>
          )}
        </motion.div>
      </div>

      {/* Tabla de envíos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Historial de Envíos</h3>
          <span className="text-sm text-muted-foreground">{filteredShipments.length} registros</span>
        </div>
        
        {filteredShipments.length === 0 ? (
          <div className="p-12 text-center">
            <Send className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No hay envíos registrados</h3>
            <p className="text-muted-foreground mt-1">Los envíos aparecerán aquí cuando se registren desde las bodegas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-semibold">Fecha / Hora</th>
                  <th className="px-6 py-3 text-left font-semibold">Local</th>
                  <th className="px-6 py-3 text-left font-semibold">Bodega</th>
                  <th className="px-6 py-3 text-center font-semibold">Productos</th>
                  <th className="px-6 py-3 text-center font-semibold">Cantidad</th>
                  <th className="px-6 py-3 text-center font-semibold">Estado</th>
                  <th className="px-6 py-3 text-left font-semibold">Notas</th>
                  <th className="px-6 py-3 text-right font-semibold">Justo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredShipments.slice(0, 50).map((shipment, index) => (
                  <motion.tr
                    key={shipment.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="hover:bg-slate-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">
                            {formatDateShort(shipment.created_at)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatTime(shipment.created_at)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-primary" />
                        <span className="font-medium">{shipment.location_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {shipment.warehouse_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold">{shipment.total_items}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-primary">{shipment.total_quantity}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {shipment.status === 'cancelled' ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          <X className="w-3 h-3 mr-1" />
                          Cancelado
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Entregado
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {shipment.notes ? (
                        <span className="text-sm text-slate-500 italic truncate max-w-[200px] block">
                          "{shipment.notes}"
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {shipment.status !== 'cancelled' && <ExportJustoButton tipo="shipment" id={shipment.id} />}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminShipmentsPage;
